/**
 * Pan/zoom maths for the full-screen diagram viewer. Pure so it can be tested
 * without a DOM: the viewer feeds it pointer positions in stage pixels and
 * applies the result as `translate(x, y) scale(s)` with origin 0 0.
 *
 * The content fills the stage exactly at s = 1, so the only valid pans keep
 * the scaled content covering the stage: x ∈ [W − W·s, 0], same for y.
 */
export interface ZoomState {
  s: number;
  x: number;
  y: number;
}

export const ZOOM_MIN = 1;
export const ZOOM_MAX = 5;
/** Double-tap on an unzoomed diagram jumps to this. */
export const ZOOM_TAP = 2.5;

export const ZOOM_IDENTITY: ZoomState = { s: 1, x: 0, y: 0 };

export function clampZoom(z: ZoomState, W: number, H: number): ZoomState {
  const s = Math.min(ZOOM_MAX, Math.max(ZOOM_MIN, z.s));
  const x = Math.min(0, Math.max(W - W * s, z.x));
  const y = Math.min(0, Math.max(H - H * s, z.y));
  return { s, x, y };
}

/** Scale `z` by `factor`, keeping the stage point (px, py) fixed under the finger. */
export function zoomAbout(z: ZoomState, factor: number, px: number, py: number, W: number, H: number): ZoomState {
  const s = Math.min(ZOOM_MAX, Math.max(ZOOM_MIN, z.s * factor));
  const k = s / z.s;
  return clampZoom({ s, x: px - (px - z.x) * k, y: py - (py - z.y) * k }, W, H);
}

export function panBy(z: ZoomState, dx: number, dy: number, W: number, H: number): ZoomState {
  return clampZoom({ s: z.s, x: z.x + dx, y: z.y + dy }, W, H);
}

/** Double-tap: zoom in on the tapped point, or back out if already zoomed. */
export function toggleTapZoom(z: ZoomState, px: number, py: number, W: number, H: number): ZoomState {
  return z.s > 1.05 ? ZOOM_IDENTITY : zoomAbout(ZOOM_IDENTITY, ZOOM_TAP, px, py, W, H);
}

// ─── Text size (period sheet) ───────────────────────────────────────────────
/** Text is resized, not magnified, so it re-wraps to the screen and never needs panning. */
export const TEXT_SCALE_MIN = 0.85;
export const TEXT_SCALE_MAX = 2.2;
export const TEXT_SCALE_STEP = 1.15;
/** Per-device setting (kv, never synced): a coach's eyes, not the team's. */
export const TEXT_SCALE_KEY = 'text_scale';

export function clampTextScale(s: number): number {
  if (!Number.isFinite(s)) return 1;
  return Math.round(Math.min(TEXT_SCALE_MAX, Math.max(TEXT_SCALE_MIN, s)) * 100) / 100;
}
